import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { trackEvent } from "../utils/analytics";
import LandingTimesheetDemo from "../components/LandingTimesheetDemo";
import HomePayCalculator from "../components/HomePayCalculator";

const SCREENSHOT_SRCS = ["timesheet", "payroll", "employees", "social"];
const PAIN_ICONS = ["file-earmark-excel", "clock-history", "calculator", "people", "shield-exclamation", "phone"];
const TEAM_ICONS = ["building", "people-fill", "person-badge", "person-circle"];
const IOS_APP_STORE_URL = "https://apps.apple.com/app/id6782426933";
const ANDROID_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=kz.tabel.mobile";

function FaqItem({ q, a, idx, open, onToggle }) {
  return (
    <div className="border-bottom" data-testid={`faq-item-${idx}`}>
      <button type="button"
              className="btn btn-link w-100 d-flex align-items-start text-start text-decoration-none text-dark py-3 px-0"
              onClick={() => onToggle(idx)}
              aria-expanded={open}
              aria-controls={`faq-answer-${idx}`}
              data-testid={`faq-button-${idx}`}>
        <span className="flex-grow-1 pe-3"><strong>{q}</strong></span>
        <i className={`bi ${open ? "bi-dash-circle-fill text-primary" : "bi-plus-circle text-muted"} fs-4`}></i>
      </button>
      {open && (
        <div id={`faq-answer-${idx}`} className="pb-3 text-muted" style={{ lineHeight: 1.7 }}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);

  // Arrays come from i18n with returnObjects.
  const screenshots = t("landing.screenshots", { returnObjects: true }) || [];
  const pains       = t("landing.pains",       { returnObjects: true }) || [];
  const teamRoles   = t("landing.team.roles", { returnObjects: true }) || [];
  const faq         = t("landing.faq.items",   { returnObjects: true }) || [];

  // SEO: title + <html lang="..."> for the landing depends on the active language.
  useEffect(() => {
    const origTitle = document.title;
    const origLang = document.documentElement.lang;
    document.title = t("landing.page_title");
    document.documentElement.lang = i18n.language || "ru";
    return () => {
      document.title = origTitle;
      document.documentElement.lang = origLang;
    };
  }, [t, i18n.language]);

  const ctaPrimary = user ? "/dashboard" : "/register";
  const ctaPrimaryText = user ? t("landing.cta.open_dashboard") : t("landing.cta.create_company");
  const trackCta = (label) => () => trackEvent("cta_click", { label });

  return (
    <div className="bg-white" data-testid="landing-page">
      {/* ---------- NAVBAR ---------- */}
      <nav className="navbar navbar-light bg-white border-bottom sticky-top" style={{ zIndex: 100 }}>
        <div className="container">
          <Link to="/" className="navbar-brand d-flex align-items-center" data-testid="landing-brand">
            <img src="/tabel-icon.png" alt="" width="32" height="32" className="me-2" />
            <strong style={{ color: "#0b3d91", letterSpacing: "0.5px" }}>Tabel.kz</strong>
          </Link>
          <div className="d-flex gap-2 align-items-center">
            <a href="#demo" className="text-decoration-none text-muted small d-none d-md-inline fw-semibold" style={{ color: "#0b3d91" }}>{t("landing.nav.demo")}</a>
            <a href="#pains" className="text-decoration-none text-muted small d-none d-md-inline">{t("landing.nav.pains")}</a>
            <a href="#calc" className="text-decoration-none text-muted small d-none d-md-inline">{t("landing.nav.calc")}</a>
            <a href="#ecp" className="text-decoration-none small d-none d-md-inline fw-semibold" style={{ color: "#0b3d91" }} data-testid="landing-nav-ecp">
              <i className="bi bi-shield-lock me-1"></i>ЭЦП
            </a>
            <a href="#faq" className="text-decoration-none text-muted small d-none d-md-inline">{t("landing.nav.faq")}</a>
            <LanguageSwitcher />
            {!loading && user ? (
              <Link to="/dashboard" className="btn btn-primary btn-sm" data-testid="landing-go-app">
                <i className="bi bi-box-arrow-in-right me-1"></i>{t("landing.nav.go_app")}
              </Link>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline-primary btn-sm" data-testid="landing-login-btn">{t("landing.nav.login")}</Link>
                <Link to="/register" className="btn btn-primary btn-sm" data-testid="landing-register-btn" onClick={trackCta("nav_register")}>{t("landing.nav.register")}</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ---------- HERO (pain-first) ---------- */}
      <header className="brand-gradient text-white" style={{ padding: "60px 0 50px" }}>
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8 mx-auto text-center">
              <div className="badge bg-light text-primary mb-3 px-3 py-2" style={{ fontSize: 13 }}>
                <i className="bi bi-shield-check me-1"></i>{t("landing.hero.badge")}
              </div>
              <h1 className="display-5 fw-bold lh-sm mb-3" data-testid="landing-h1">
                {t("landing.hero.h1")}
              </h1>
              <p className="fs-5 fw-semibold opacity-90 mb-2 mx-auto" style={{ maxWidth: 720 }}>
                {t("landing.hero.tagline")}
              </p>
              <p className="lead opacity-75 mb-4 mx-auto" style={{ maxWidth: 720 }}>{t("landing.hero.subtitle")}</p>
              <div className="d-flex flex-wrap gap-3 align-items-center justify-content-center">
                <a href="#demo" className="btn btn-warning btn-lg px-4 fw-bold text-dark" data-testid="landing-cta-try-demo">
                  <i className="bi bi-play-circle me-2"></i>{t("landing.hero.try_demo")}
                </a>
                <Link to={ctaPrimary} className="btn btn-light btn-lg px-4" data-testid="landing-cta-primary" onClick={trackCta("hero_primary")}>
                  <i className="bi bi-rocket-takeoff me-2"></i>{ctaPrimaryText}
                </Link>
              </div>
              <div className="mt-4 d-flex flex-column align-items-center gap-2">
                <span className="small opacity-75">{t("landing.apps.label")}</span>
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  <a href={IOS_APP_STORE_URL}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="btn btn-outline-light px-4"
                     data-testid="landing-ios-download"
                     onClick={trackCta("ios_app_store")}>
                    <i className="bi bi-apple me-2"></i>{t("landing.apps.ios")}
                  </a>
                  <a href={ANDROID_PLAY_STORE_URL}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="btn btn-outline-light px-4"
                     data-testid="landing-android-download"
                     onClick={trackCta("android_play_store")}>
                    <i className="bi bi-google-play me-2"></i>{t("landing.apps.android")}
                  </a>
                </div>
                <span className="small opacity-75">{t("landing.apps.hint")}</span>
              </div>
              <div className="mt-4 d-flex flex-wrap gap-3 justify-content-center small opacity-90">
                <span><i className="bi bi-x-circle-fill text-warning me-1"></i>{t("landing.hero.bullet1")}</span>
                <span><i className="bi bi-x-circle-fill text-warning me-1"></i>{t("landing.hero.bullet2")}</span>
                <span><i className="bi bi-x-circle-fill text-warning me-1"></i>{t("landing.hero.bullet3")}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ---------- LIVE DEMO ---------- */}
      {!user && (
        <LandingTimesheetDemo registerHref={ctaPrimary} onRegisterClick={trackCta("demo_register")} />
      )}

      {/* ---------- CALCULATOR (для авторизованных; гости видят его в демо-блоке) ---------- */}
      {user && (
        <section id="calc" className="py-5" style={{ scrollMarginTop: 80 }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="text-center mb-4">
                  <h2 className="fw-bold mb-2">{t("landing.calc.section_title")}</h2>
                  <p className="text-muted mb-0">{t("landing.calc.section_subtitle")}</p>
                </div>
                <HomePayCalculator />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ---------- PAINS ---------- */}
      <section id="pains" className="py-5 bg-light" style={{ scrollMarginTop: 80 }}>
        <div className="container py-4">
          <h2 className="text-center fw-bold mb-2" data-testid="landing-h2-pains">{t("landing.pains_title")}</h2>
          <p className="text-center text-muted mb-5">{t("landing.pains_subtitle")}</p>
          <div className="row g-4">
            {pains.map((p, i) => (
              <div key={i} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body">
                    <i className={`bi bi-${PAIN_ICONS[i] || "exclamation-circle"} fs-2 text-danger mb-2 d-block`}></i>
                    <h3 className="h6 fw-bold mb-2">{p.title}</h3>
                    <p className="text-muted small mb-0">{p.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-5">
            <a href="#demo" className="btn btn-primary btn-lg">
              <i className="bi bi-play-circle me-2"></i>{t("landing.hero.try_demo")}
            </a>
          </div>
        </div>
      </section>

      {/* ---------- WHO IT'S FOR (whole company in one system) ---------- */}
      <section id="team" className="py-5" style={{ scrollMarginTop: 80 }}>
        <div className="container py-4">
          <h2 className="text-center fw-bold mb-2" data-testid="landing-h2-team">{t("landing.team.title")}</h2>
          <p className="text-center text-muted mb-5 mx-auto" style={{ maxWidth: 720 }}>
            {t("landing.team.subtitle")}
          </p>
          <div className="row g-4">
            {teamRoles.map((role, i) => (
              <div key={i} className="col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body text-center">
                    <i className={`bi bi-${TEAM_ICONS[i] || "person"} fs-1 text-primary mb-3 d-block`}></i>
                    <h3 className="h6 fw-bold mb-2">{role.title}</h3>
                    <p className="text-muted small mb-0">{role.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-muted mt-4 mb-0 small">
            <i className="bi bi-diagram-3 me-1 text-primary"></i>{t("landing.team.note")}
          </p>
        </div>
      </section>

      {/* ---------- HOW IT WORKS (with screenshots) ---------- */}
      <section id="how" className="py-5 bg-light" style={{ scrollMarginTop: 80 }}>
        <div className="container py-4">
          <h2 className="text-center fw-bold mb-2" data-testid="landing-h2-how">{t("landing.how_title")}</h2>
          <p className="text-center text-muted mb-5">{t("landing.how_subtitle")}</p>
          {screenshots.map((s, i) => (
            <div key={i} className={`row align-items-center mb-5 g-5 ${i % 2 ? "flex-row-reverse" : ""}`}>
              <div className="col-lg-6">
                <div className="d-inline-block badge bg-primary mb-2 px-2 py-1">{t("landing.step", { n: i + 1 })}</div>
                <h3 className="fw-bold mb-3">{s.title}</h3>
                <p className="text-muted" style={{ lineHeight: 1.7 }}>{s.desc}</p>
              </div>
              <div className="col-lg-6">
                <img src={`/landing/${SCREENSHOT_SRCS[i]}.png`} alt={s.alt}
                     className="img-fluid rounded shadow-sm border"
                     loading="lazy"
                     style={{ background: "#fff" }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- KEYWORDS BLOCK (SEO content) ---------- */}
      <section className="py-5">
        <div className="container py-4">
          <div className="row g-5">
            <div className="col-md-6">
              <h2 className="fw-bold mb-3">{t("landing.seo.template_title")}</h2>
              <p className="text-muted">{t("landing.seo.template_p1")}</p>
              <p className="text-muted mb-0">{t("landing.seo.template_p2")}</p>
            </div>
            <div className="col-md-6">
              <h2 className="fw-bold mb-3">{t("landing.seo.calc_title")}</h2>
              <p className="text-muted">{t("landing.seo.calc_p1")}</p>
              <p className="text-muted mb-0">{t("landing.seo.calc_p2")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- ECP / Digital Signature USP ---------- */}
      <section id="ecp" className="py-5" style={{
        scrollMarginTop: 80,
        background: "linear-gradient(135deg, #0b3d91 0%, #1e5fd1 50%, #2d7dff 100%)",
        color: "white",
      }} data-testid="landing-ecp-section">
        <div className="container py-4">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-3" data-testid="landing-ecp-title">
              {t("landing.ecp.title")}
            </h2>
            <p className="lead opacity-90 mx-auto" style={{ maxWidth: 720 }}>
              {t("landing.ecp.subtitle")}
            </p>
          </div>

          <div className="row g-4 mb-5">
            {[
              { icon: "bi-lightning-charge-fill", k: "card1" },
              { icon: "bi-arrow-right-circle-fill", k: "card2" },
              { icon: "bi-shield-fill-check", k: "card3" },
            ].map((c) => (
              <div className="col-md-4" key={c.k}>
                <div className="h-100 p-4 rounded-3"
                     style={{ background: "rgba(255,255,255,0.10)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.18)" }}
                     data-testid={`landing-ecp-${c.k}`}>
                  <div className="mb-3" style={{ fontSize: "2rem", color: "#ffd560" }}>
                    <i className={`bi ${c.icon}`}></i>
                  </div>
                  <h5 className="fw-bold mb-2">{t(`landing.ecp.${c.k}_title`)}</h5>
                  <p className="mb-0 opacity-90 small">{t(`landing.ecp.${c.k}_text`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="row align-items-center g-4">
            <div className="col-md-7">
              <h4 className="fw-bold mb-3">{t("landing.ecp.how_title")}</h4>
              <ol className="ps-0" style={{ listStyle: "none", counterReset: "step" }}>
                {["step1", "step2", "step3"].map((s) => (
                  <li key={s}
                      className="d-flex align-items-start gap-3 mb-3"
                      style={{ counterIncrement: "step" }}
                      data-testid={`landing-ecp-${s}`}>
                    <div className="flex-shrink-0 d-flex align-items-center justify-content-center fw-bold"
                         style={{
                           width: 36, height: 36, borderRadius: "50%",
                           background: "#ffd560", color: "#0b3d91", fontSize: "1.05rem",
                         }}>
                      {s.replace("step", "")}
                    </div>
                    <div className="pt-1">{t(`landing.ecp.${s}`)}</div>
                  </li>
                ))}
              </ol>
              <p className="small opacity-75 mb-0 mt-3">
                <i className="bi bi-info-circle me-1"></i>
                {t("landing.ecp.footer_note")}
              </p>
            </div>
            <div className="col-md-5 text-center">
              <div className="d-grid gap-2" style={{ maxWidth: 360, margin: "0 auto" }}>
                <Link to="/register" className="btn btn-warning btn-lg fw-bold text-dark"
                      data-testid="landing-ecp-cta-primary"
                      onClick={trackCta("ecp_register")}>
                  <i className="bi bi-shield-lock me-2"></i>
                  {t("landing.ecp.cta_primary")}
                </Link>
                <a href="https://pki.gov.kz/ncalayer-2/" target="_blank" rel="noopener noreferrer"
                   className="btn btn-outline-light"
                   data-testid="landing-ecp-cta-secondary">
                  <i className="bi bi-download me-2"></i>
                  {t("landing.ecp.cta_secondary")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" className="py-5 bg-light" style={{ scrollMarginTop: 80 }}>
        <div className="container py-4" style={{ maxWidth: 820 }}>
          <h2 className="text-center fw-bold mb-2" data-testid="landing-h2-faq">{t("landing.faq.title")}</h2>
          <p className="text-center text-muted mb-4">{t("landing.faq.subtitle")}</p>
          <div className="card border-0 shadow-sm">
            <div className="card-body px-4">
              {faq.map(({ q, a }, i) => (
                <FaqItem key={i} idx={i} q={q} a={a}
                         open={openFaq === i}
                         onToggle={(n) => setOpenFaq(openFaq === n ? -1 : n)} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="py-5 brand-gradient text-white text-center">
        <div className="container py-4">
          <h2 className="fw-bold mb-3">{t("landing.final_cta.title")}</h2>
          <p className="lead opacity-90 mb-4" style={{ maxWidth: 600, margin: "0 auto" }}>
            {t("landing.final_cta.subtitle")}
          </p>
          <button type="button"
                  className="btn btn-light btn-lg px-5 fw-semibold"
                  onClick={() => navigate(ctaPrimary)}
                  data-testid="landing-cta-final">
            <i className="bi bi-rocket-takeoff me-2"></i>{ctaPrimaryText}
          </button>
          <div className="small mt-3 opacity-75">
            {t("landing.final_cta.have_account")} <Link to="/login" className="text-white text-decoration-underline">{t("landing.nav.login")}</Link>
          </div>
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="py-4 border-top">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 text-center text-md-start mb-2 mb-md-0">
              <strong style={{ color: "#0b3d91" }}>Tabel.kz</strong>
              <span className="text-muted small ms-2">© {new Date().getFullYear()} {t("landing.footer.rights")}</span>
            </div>
            <div className="col-md-6 text-center text-md-end small text-muted">
              <a href={IOS_APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                 className="text-muted text-decoration-none" data-testid="footer-ios-download">
                <i className="bi bi-apple me-1"></i>{t("landing.apps.ios_short")}
              </a>
              <span className="mx-2">·</span>
              <a href={ANDROID_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
                 className="text-muted text-decoration-none" data-testid="footer-android-download">
                <i className="bi bi-google-play me-1"></i>{t("landing.apps.android_short")}
              </a>
              <span className="mx-2">·</span>
              {t("landing.footer.tagline")}
              <span className="mx-2">·</span>
              <Link to="/terms" className="text-muted" data-testid="footer-terms-link">{t("legal.terms_link")}</Link>
              <span className="mx-2">·</span>
              <Link to="/privacy" className="text-muted" data-testid="footer-privacy-link">{t("legal.privacy_link")}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
